import React, { memo } from 'react';
import { useWatch } from 'react-hook-form';
import InputComponent from './InputMC';
/**
 * @fileoverview Componente InputComponentConditionalMC - Renderiza un input condicionalmente según el valor de un campo observado, optimizando el renderizado y desacoplando la lógica del formulario principal.
 * @module InputComponentConditionalMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 2.0.0 - Separación de lógica y optimización de renders
 *
 * @typedef {Object} InputComponentConditionalMCProps
 * @property {Object} formMethods - Métodos de react-hook-form (debe incluir control)
 * @property {string} fieldName - Nombre del campo a observar (ej: 'paymentMethod')
 * @property {string|number|boolean} showWhenValue - Valor que debe tener el campo para mostrar el input
 * @property {string} inputName - Nombre del input a renderizar (ej: 'paymentDescription')
 * @property {string} label - Etiqueta del input
 * @property {string} [placeholder] - Placeholder opcional
 * @property {string} [className] - Clases adicionales para el input
 *
 * @component InputComponentConditionalMC
 * @category Component - Molecule
 *
 * @description
 * Componente genérico y memoizado que observa un campo específico del formulario usando `useWatch` de react-hook-form y solo renderiza el input cuando el valor coincide con `showWhenValue`.
 *
 * **Ventajas de arquitectura:**
 * - Desacopla la lógica condicional del formulario principal.
 * - Evita renders innecesarios del componente padre: solo este subcomponente se re-renderiza cuando cambia el campo observado.
 * - Permite máxima reutilización para cualquier campo condicional.
 * - Mejora la performance en formularios grandes o con muchos campos dinámicos.
 *
 * **Props Interface:**
 * ```javascript
 * {
 *   formMethods: object, // Métodos de react-hook-form (debe incluir control)
 *   fieldName: string,   // Campo a observar (ej: 'paymentMethod')
 *   showWhenValue: string|number|boolean, // Valor que activa el input
 *   inputName: string,   // Nombre del input a renderizar
 *   label: string,       // Etiqueta del input
 *   placeholder?: string,
 *   className?: string
 * }
 * ```
 *
 * @param {InputComponentConditionalMCProps} props - Propiedades del componente
 * @returns {JSX.Element|null} El input renderizado o null si la condición no se cumple
 *
 * @example
 * // Renderiza un input solo si paymentMethod === 'other'
 * <InputComponentConditionalMC
 *   formMethods={formMethods}
 *   fieldName="paymentMethod"
 *   showWhenValue="other"
 *   inputName="paymentDescription"
 *   label="Otro"
 *   placeholder="Indica el método de pago"
 * />
 *
 * @see {@link https://react-hook-form.com/docs/usewatch} Documentación de useWatch
 * @see {@link InputMC} Componente de input base
 */
const InputComponentConditionalMC = memo(({ formMethods, fieldName, showWhenValue, inputName, label, placeholder = '', className = '' }) => {

  const { control } = formMethods;
  const fieldValue = useWatch({ control, name: fieldName });

  return (
    fieldValue === showWhenValue ? (
      <InputComponent
        useFloatingLabel
        name={inputName}
        label={label}
        placeholder={placeholder}
        className={className}
      />
    ) : null
  );
});

InputComponentConditionalMC.displayName = 'InputComponentConditionalMC';

export default InputComponentConditionalMC;